import { Button, PageHeader, cn } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import {
  INBOX_POLL_MS,
  categoryLabels,
  inboxQueryKey,
  listInbox,
  markInboxRead,
  type InboxItem,
} from "./inbox-api.js";
import { inboxTarget } from "./inbox-links.js";

const when = new Intl.DateTimeFormat("en-NZ", {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "short",
});

function InboxRow({
  item,
  onOpen,
}: {
  item: InboxItem;
  onOpen: (item: InboxItem) => void;
}) {
  const target = inboxTarget(item.deep_link);
  const content = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          "mt-1.5 size-2.5 shrink-0 rounded-full",
          item.read_at ? "bg-stone-200" : "bg-emerald-700",
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "text-sm",
              item.read_at ? "font-semibold" : "font-bold",
            )}
          >
            {item.title}
          </span>
          {!item.read_at ? <span className="sr-only">Unread</span> : null}
        </span>
        {item.body ? (
          <span className="mt-1 block text-sm text-stone-600">{item.body}</span>
        ) : null}
        <span className="mt-1.5 block text-xs font-semibold text-stone-500">
          {categoryLabels[item.category]} ·{" "}
          {when.format(new Date(item.created_at))}
        </span>
      </span>
      {target ? (
        <ChevronRight
          aria-hidden="true"
          className="mt-1 size-4 shrink-0 text-stone-400"
        />
      ) : null}
    </>
  );
  const className =
    "flex w-full items-start gap-3 px-4 py-4 text-left transition hover:bg-stone-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-800 sm:px-5";

  if (target?.kind === "href") {
    return (
      <a className={className} href={target.href} onClick={() => onOpen(item)}>
        {content}
      </a>
    );
  }
  return (
    <button className={className} onClick={() => onOpen(item)} type="button">
      {content}
    </button>
  );
}

export function InboxPage() {
  const shell = useShell();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const queryKey = inboxQueryKey(shell.activeOrganisationId);
  const inbox = useQuery({
    queryFn: () => listInbox(shell.activeOrganisationId),
    queryKey,
    refetchInterval: INBOX_POLL_MS,
  });
  const markRead = useMutation({
    mutationFn: markInboxRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
  const unread = inbox.data?.items.filter((item) => !item.read_at) ?? [];

  function open(item: InboxItem) {
    if (!item.read_at) markRead.mutate([item.id]);
    const target = inboxTarget(item.deep_link);
    if (target?.kind === "route") {
      void navigate({ search: target.search, to: target.to });
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        actions={
          unread.length > 0 ? (
            <Button
              disabled={markRead.isPending}
              onClick={() => markRead.mutate(unread.map((item) => item.id))}
              size="sm"
              variant="outline"
            >
              Mark all read
            </Button>
          ) : null
        }
        title="Inbox"
      />
      {inbox.isPending ? (
        <LoadingState label="Loading" />
      ) : inbox.error ? (
        <ErrorState
          message={displayError(inbox.error)}
          onAction={() => void inbox.refetch()}
          title="Inbox didn’t load"
        />
      ) : inbox.data.items.length === 0 ? (
        <EmptyState title="Nothing in your inbox" />
      ) : (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-2xl border border-stone-200 bg-surface">
          {inbox.data.items.map((item) => (
            <li key={item.id}>
              <InboxRow item={item} onOpen={open} />
            </li>
          ))}
        </ul>
      )}
      {markRead.error ? (
        <p className="text-sm font-semibold text-red-800" role="alert">
          {displayError(markRead.error)}
        </p>
      ) : null}
    </div>
  );
}
